import {
  BackendExceptionResponse,
  CsvImportResponse,
  PagedRequest,
  PagedResponse,
  ProductImportResponse,
  ProductListFilter,
  ProductListItem,
  ProductsFilterData,
} from './apiTypes';
import api from './base';
import {
  mockFullImport,
  mockGetProductsFilterData,
  mockGetProductsList,
  mockPartialImport,
  mockProductExport,
} from './mocks/productsMocks';
import { ApiGroup } from './model/ApiGroup';

const PRODUCTS_URL = 'management/products';
const PRODUCTS_FULL_IMPORT = 'management/products/import/full';
const OFFERS_FULL_IMPORT = 'management/products/import/full/offers';
const FILTER_DATA_URL = 'management/products/filter-data';
export const IMPORT_MEDIA_URL = 'management/products/import/media-urls';
export const EXPORT_MEDIA_URL = 'management/products/export/media-urls';
export const IMPORT_CATEGORIES_URL = 'management/products/import/category-parameters';
export const EXPORT_CATEGORIES_URL = 'management/products/export/category-parameters';
export const IMPORT_DELIVERY_DELAY_URL = 'management/products/import/delivery-delays';
export const EXPORT_DELIVERY_DELAY_URL = 'management/products/export/delivery-delays';
export const IMPORT_BRANDS_URL = 'management/products/import/brand-descriptions';
export const EXPORT_BRANDS_URL = 'management/products/export/brand-descriptions';
export const IMPORT_LOGISTICS_URL = 'management/products/import/logistics';
export const EXPORT_LOGISTICS_URL = 'management/products/export/logistics';
export const IMPORT_ALZA_ATTRIBUTES_URL = 'management/products/import/alza-attributes';
export const STOCKS_UPDATE_URL = 'management/products/availability/stock';
export const PRICES_UPDATE_URL = 'management/products/availability/price';

export async function importProducts(
  url: string,
  file: FormData,
  partnerCode: string | null = null,
) {
  return api.post<CsvImportResponse | string | BackendExceptionResponse>(url, file, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: { partnerCode },
  });
}

api.mock.onPost(IMPORT_BRANDS_URL).reply(200, mockPartialImport);
api.mock.onPost(IMPORT_CATEGORIES_URL).reply(200, mockPartialImport);
api.mock.onPost(IMPORT_MEDIA_URL).reply(200, mockPartialImport);
api.mock.onPost(IMPORT_LOGISTICS_URL).reply(200, mockPartialImport);
api.mock.onPost(IMPORT_DELIVERY_DELAY_URL).reply(200, mockPartialImport);
api.mock.onPost(IMPORT_ALZA_ATTRIBUTES_URL).reply(200, mockPartialImport);

export async function fullImportProducts(file: FormData) {
  return api.post<ProductImportResponse>(PRODUCTS_FULL_IMPORT, file, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function fullImportOffers(file: FormData, apiGroup: ApiGroup) {
  return api.post<void>(`${OFFERS_FULL_IMPORT}/${apiGroup}`, file, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

const fullImportError = {
  httpStatus: 400,
  code: 'VALIDATION_ERROR',
  codeDescription: 'Validation of request failed',
  details: 'Input file contains duplicate product_id',
};

let useErrorResponseOnce = true;

api.mock.onPost(PRODUCTS_FULL_IMPORT).reply(() => {
  if (useErrorResponseOnce) {
    useErrorResponseOnce = false;
    return [400, JSON.stringify(fullImportError)];
  }

  return [200, JSON.stringify(mockFullImport)];
});
api.mock.onPost(/\/management\/products\/import\/full\/offers\/.+/).reply(200, mockFullImport);

export async function deleteProducts(file: FormData) {
  return api.delete<string | BackendExceptionResponse>(PRODUCTS_URL, {
    headers: { 'Content-Type': 'multipart/form-data' },
    data: file,
  });
}

api.mock.onDelete(PRODUCTS_URL).reply(200, '');

export async function exportProducts(url: string, filters: any) {
  return api.post<string | BackendExceptionResponse>(url, filters);
}

api.mock.onPost(EXPORT_BRANDS_URL).reply(200, '');
api.mock.onPost(EXPORT_CATEGORIES_URL).reply(200, '');
api.mock.onPost(EXPORT_MEDIA_URL).reply(200, '');
api.mock.onPost(EXPORT_LOGISTICS_URL).reply(200, '');
api.mock.onPost(EXPORT_DELIVERY_DELAY_URL).reply(200, '');

export async function getProductList(
  apiGroup: string,
  request: PagedRequest<ProductListFilter>,
  signal?: AbortSignal,
) {
  return api.post<PagedResponse<ProductListItem>>(`${PRODUCTS_URL}/api-groups/${apiGroup}`, request, { signal });
}

api.mock.onPost(/\/management\/products\/api-groups\/.+/).reply(200, mockGetProductsList);

export async function exportProductList(
  apiGroup: ApiGroup,
  request: PagedRequest<ProductListFilter>,
) {
  return api.post<string | BackendExceptionResponse>(`management/products/export/api-groups/${apiGroup}`, request);
}

api.mock.onPost(/\/management\/products\/export\/api-groups\/.+/).reply(200, mockProductExport);

export async function removeProductFromPricing(eshopProductId: number) {
  return api.delete<void>(`${PRODUCTS_URL}/${eshopProductId}/pricing`);
}

api.mock.onDelete(/\/management\/products\/\d+\/pricing/).reply(200);

export async function getProductsFilterData(
  apiGroup: ApiGroup,
  signal?: AbortSignal,
): Promise<ProductsFilterData> {
  return api.get<ProductsFilterData>(
    `${FILTER_DATA_URL}/${apiGroup}`,
    { signal },
  )
    .then(response => response.data);
}

api.mock.onGet(/\/management\/products\/filter-data\/.+/).reply(200, mockGetProductsFilterData);
